/**
 * ContactForm Component
 * 
 * A secure contact form with built-in spam protection:
 * - Honeypot field (hidden from humans, bots fill it)
 * - Time-based validation (tracks when form loaded)
 * - Client-side validation
 * - Loading states and error handling
 */

import React, { useState, useEffect, useRef } from 'react';

// API endpoint - update this after deploying Lambda
const API_ENDPOINT = process.env.REACT_APP_CONTACT_API_URL || 'https://YOUR_API_GATEWAY_URL/contact';

const ContactForm = () => {
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
        website: '', // Honeypot field
    });
    
    // UI state
    const [status, setStatus] = useState('idle'); // idle | loading | success | error
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    
    // Spam protection: track when form was loaded
    const formLoadTime = useRef(Date.now());

    // Reset form load time when component mounts
    useEffect(() => {
        formLoadTime.current = Date.now();
    }, []);

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        if (status === 'error') {
            setStatus('idle');
            setErrorMessage('');
        }
    };

    // Client-side validation
    const validateForm = () => {
        if (!formData.name.trim() || formData.name.trim().length < 2) {
            setErrorMessage('Please enter your name.');
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim() || !emailRegex.test(formData.email)) {
            setErrorMessage('Please enter a valid email address.');
            return false;
        }

        if (!formData.message.trim() || formData.message.trim().length < 10) {
            setErrorMessage('Please provide more details about your project (at least 10 characters).');
            return false;
        }

        return true;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form
        if (!validateForm()) {
            setStatus('error');
            return;
        }

        setStatus('loading');
        setErrorMessage('');

        try {
            const response = await fetch(API_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: formData.name.trim(),
                    email: formData.email.trim().toLowerCase(),
                    phone: formData.phone.trim(),
                    message: formData.message.trim(),
                    website: formData.website, // Honeypot
                    _formLoadTime: formLoadTime.current, // Time-based validation
                }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setStatus('success');
                setSuccessMessage(data.message || 'Thank you! We will be in touch soon.');
                
                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    message: '',
                    website: '',
                });
                
                // Reset load time for potential re-submission
                formLoadTime.current = Date.now();
            } else {
                setStatus('error');
                setErrorMessage(data.error || 'Something went wrong. Please try again.');
            }
        } catch (error) {
            console.error('Contact form error:', error);
            setStatus('error');
            setErrorMessage('Unable to send message. Please call us at (207) 588-7000.');
        }
    };

    return (
        <div className="contact-form-container">
            <h3 className="contact-form-title">Request a Consultation</h3>
            
            {status === 'success' ? (
                <div className="contact-form-success">
                    <div className="success-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                    </div>
                    <p className="success-message">{successMessage}</p>
                    <button 
                        type="button"
                        className="btn-secondary-custom"
                        onClick={() => setStatus('idle')}
                    >
                        Send Another Message
                    </button>
                </div>
            ) : (
                <form onSubmit={handleSubmit} noValidate>
                    {/* Honeypot field - hidden from humans */}
                    <div 
                        style={{ 
                            position: 'absolute', 
                            left: '-9999px', 
                            opacity: 0,
                            height: 0,
                            overflow: 'hidden'
                        }}
                        aria-hidden="true"
                    >
                        <label htmlFor="website">Website</label>
                        <input
                            type="text"
                            id="website"
                            name="website"
                            value={formData.website}
                            onChange={handleChange}
                            tabIndex={-1}
                            autoComplete="off"
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="name" className="form-label-custom">
                            Your Name <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            className="form-control-custom"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            maxLength={100}
                            disabled={status === 'loading'}
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="email" className="form-label-custom">
                            Email Address <span className="required">*</span>
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            className="form-control-custom"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            maxLength={254}
                            disabled={status === 'loading'}
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="phone" className="form-label-custom">
                            Phone Number
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            className="form-control-custom"
                            value={formData.phone}
                            onChange={handleChange}
                            maxLength={20}
                            disabled={status === 'loading'}
                        />
                    </div>

                    <div className="mb-4">
                        <label htmlFor="message" className="form-label-custom">
                            Project Details <span className="required">*</span>
                        </label>
                        <textarea
                            id="message"
                            name="message"
                            rows="4"
                            className="form-control-custom"
                            placeholder="Tell us about your restoration project..."
                            value={formData.message}
                            onChange={handleChange}
                            required
                            maxLength={5000}
                            disabled={status === 'loading'}
                        />
                        <small className="character-count">
                            {formData.message.length}/5000 characters
                        </small>
                    </div>

                    {status === 'error' && errorMessage && (
                        <div className="contact-form-error">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span>{errorMessage}</span>
                        </div>
                    )}

                    <button 
                        type="submit" 
                        className="btn-primary-custom w-100"
                        disabled={status === 'loading'}
                    >
                        {status === 'loading' ? (
                            <>
                                <span className="spinner"></span>
                                Sending...
                            </>
                        ) : (
                            'Send Inquiry'
                        )}
                    </button>
                </form>
            )}
        </div>
    );
};

export default ContactForm;
